import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { translateObject, translateArray } from './translateService';

/**
 * Custom hook for translating backend data on language change
 * 
 * @param {Object|Array|string} data - Data to be translated
 * @param {Object} options - Additional options
 * @returns {Object} - Translated data and loading state
 */
export const useBackendTranslation = (data, options = {}) => {
  const { i18n } = useTranslation();
  const [translatedData, setTranslatedData] = useState(data);
  const [isLoading, setIsLoading] = useState(false);
  const currentLang = i18n.language;
  
  // Keep track of the latest data to prevent race conditions
  const dataRef = useRef(data);
  const requestIdRef = useRef(0);
  const optionsRef = useRef(options);
  
  // Update optionsRef when options change
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);
  
  const translateData = useCallback(async () => {
    // Store current data and create a unique request ID
    const currentData = dataRef.current;
    const requestId = ++requestIdRef.current;
    const currentOptions = optionsRef.current;
    
    if (!currentData) return;

    // Skip translation for English (default language)
    if (currentLang === 'en') {
      setTranslatedData(currentData);
      return;
    }

    // Debounce to prevent too many API calls
    await new Promise(resolve => setTimeout(resolve, currentOptions.debounceMs || 1000));
    
    // If a newer request has been made, abort this one
    if (requestId !== requestIdRef.current) return;
    
    setIsLoading(true);

    try {
      let translated;
      const skipFields = currentOptions.skipFields || [];
        
      if (Array.isArray(currentData)) {
        // Clone the array to avoid modifying the original
        const dataToTranslate = [...currentData];
        
        // If we have skipFields, filter them from translation
        if (skipFields.length > 0) {
          translated = await Promise.all(
            dataToTranslate.map(async (item) => {
              if (typeof item === 'object' && item !== null) {
                const itemCopy = { ...item };
                
                // Extract fields to skip
                const skippedFieldValues = {};
                skipFields.forEach(field => {
                  if (field in itemCopy) {
                    skippedFieldValues[field] = itemCopy[field];
                    // Don't delete, just mark for later check
                  }
                });
                
                // Translate the object
                const translatedItem = await translateObject(itemCopy, currentLang);
                
                // Restore skipped fields
                Object.assign(translatedItem, skippedFieldValues);
                
                return translatedItem;
              } else if (typeof item === 'string') {
                const { translateText } = await import('./translateService');
                return await translateText(item, currentLang);
              } else {
                return item;
              }
            })
          );
        } else {
          translated = await translateArray(dataToTranslate, currentLang);
        }
      } else if (typeof currentData === 'object' && currentData !== null) {
        // Clone the object to avoid modifying the original
        const dataToTranslate = { ...currentData };
        
        // If we have skipFields, filter them from translation
        if (skipFields.length > 0) {
          const skippedFieldValues = {};
          skipFields.forEach(field => {
            if (field in dataToTranslate) {
              skippedFieldValues[field] = dataToTranslate[field];
              // Don't delete, just mark for later check
            }
          });
          
          // Translate the object
          translated = await translateObject(dataToTranslate, currentLang);
          
          // Restore skipped fields
          Object.assign(translated, skippedFieldValues);
        } else {
          translated = await translateObject(dataToTranslate, currentLang);
        }
      } else if (typeof currentData === 'string') {
        // Handle simple string translation
        const { translateText } = await import('./translateService');
        translated = await translateText(currentData, currentLang);
      } else {
        // For other types, just return as is
        translated = currentData;
      }
      
      // If a newer request has been made while we were translating, discard this result
      if (requestId !== requestIdRef.current) return;
      
      setTranslatedData(translated);
    } catch (error) {
      console.error('Error translating data:', error);
      // Fallback to original data on error
      if (requestId === requestIdRef.current) {
        setTranslatedData(currentData);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [currentLang]);

  // Update dataRef when data changes
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Translate data when language changes or data changes
  useEffect(() => {
    translateData();
  }, [translateData, data, currentLang]);

  return { translatedData, isLoading };
};

/**
 * Custom hook for translating a single text string from backend
 * 
 * @param {string} text - Text to be translated
 * @returns {Object} - Translated text and loading state
 */
export const useTextTranslation = (text) => {
  const { translatedData: translatedText, isLoading } = useBackendTranslation(text);
  return { translatedText, isLoading };
};
